import { type FC } from "react";

import { SimpleGrid, Text } from "@mantine/core";
import { Alert, Status } from "@snapchat/snap-design-system";

import { formatMilliUsd, formatMonthName } from "../../../../lens-creator-payout/utils";
import { formatWithUSFormat } from "../../../../lens-stats/formatters/formatNumbers";
import { useLensPlusEarnings } from "../../../hooks/useLensPlusEarnings";
import { ProgramPayoutStatus } from "../../../models";
import { formatMonthDay, formatMonthYear, toDisplayedSubscriptions, toUtcMonthStart } from "../../../utils";

import styles from "./EnrolledProgram.module.css";
import { LensPlusPayoutHistoryTable } from "./LensPlusPayoutHistoryTable";
import { PanelDataGuard } from "./PanelDataGuard";
import { type EnrolledPanelProps, ProgramEnrolledPanel } from "./ProgramEnrolledPanel";
import { StatTile } from "./StatTile";
import {
    LensPlusEnrolledDescription,
    LensPlusEnrolledTitle,
    LensPlusFootnote,
    LensPlusUsersTile,
    MonthScoreTile,
    MonthStatsError,
    MonthlyPayoutsSince,
    ResetsOn,
    ScoreFormula,
    TotalEarnedTile,
} from "./messages.generated";

export const LensPlusEnrolledPanel: FC<EnrolledPanelProps> = ({
    lensId,
    unlockableId,
    lensName,
    markedAsExclusiveAt,
    monetizationUrl,
    onViewInMonetization,
}) => {
    const { loading, error, scoresError, currentMonth, totalEarnedMilliUsd, paidMonths, payoutRows } =
        useLensPlusEarnings({ lensId, unlockableId, markedAsExclusiveAt });

    const month = formatMonthName(currentMonth.month);
    const { status } = currentMonth;
    const totals = scoresError ? null : currentMonth.totals;
    // k=10 anonymity: signups under 10 can't be shown, so drop the formula that names them; the score stays.
    const showsFormula = totals && toDisplayedSubscriptions(totals.subscribeCount) > 0;

    return (
        <ProgramEnrolledPanel
            title={<LensPlusEnrolledTitle />}
            description={<LensPlusEnrolledDescription />}
            monetizationUrl={monetizationUrl}
            onViewInMonetization={onViewInMonetization}
        >
            <PanelDataGuard loading={loading} error={error}>
                {scoresError && <Alert status={Status.ERROR} title={<MonthStatsError />} />}
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} className={styles.statsGrid}>
                    <StatTile
                        label={<LensPlusUsersTile month={month} />}
                        value={totals && formatWithUSFormat(totals.subscriberActionsUu)}
                        caption={
                            !scoresError &&
                            status === ProgramPayoutStatus.InProgress && (
                                <ResetsOn date={formatMonthDay(toUtcMonthStart(new Date(currentMonth.month), 1))} />
                            )
                        }
                    />
                    <StatTile
                        label={<MonthScoreTile month={month} />}
                        value={totals && formatWithUSFormat(totals.lensScore)}
                        caption={
                            showsFormula && (
                                <ScoreFormula
                                    users={formatWithUSFormat(totals.subscriberActionsUu)}
                                    signups={formatWithUSFormat(totals.subscribeCount)}
                                />
                            )
                        }
                    />
                    <StatTile
                        label={<TotalEarnedTile lensName={lensName} />}
                        value={formatMilliUsd(totalEarnedMilliUsd)}
                        caption={
                            paidMonths && (
                                <MonthlyPayoutsSince
                                    count={paidMonths.count}
                                    month={formatMonthYear(paidMonths.since)}
                                />
                            )
                        }
                    />
                </SimpleGrid>
                <LensPlusPayoutHistoryTable rows={payoutRows} />
                <Text className={styles.caption}>
                    <LensPlusFootnote />
                </Text>
            </PanelDataGuard>
        </ProgramEnrolledPanel>
    );
};
